import React, { useState, useEffect, useCallback } from 'react';
import './RsvpSubmissions.css';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const RsvpSubmissions = () => {
  const [rsvps, setRsvps] = useState([]);
  const [filteredRsvps, setFilteredRsvps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    attending: '',
    eventType: '',
    searchQuery: ''
  });

  // Fetch RSVP submissions
  const fetchRsvps = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/rsvp/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem("access_token") || ""}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setRsvps(data);
        setFilteredRsvps(data);
      } else if (response.status === 403) {
        setError('Access denied. Admin privileges required.');
      } else {
        setError('Failed to fetch RSVP submissions');
      }
    } catch (err) {
      setError('Network error while fetching RSVP submissions');
    }
    
    setLoading(false);
  };

  // Filter RSVPs based on current filters
  const applyFilters = useCallback(() => {
    let filtered = [...rsvps];

    // Date filter
    if (filters.dateFrom) {
      filtered = filtered.filter(rsvp => 
        new Date(rsvp.created_at) >= new Date(filters.dateFrom)
      );
    }

    if (filters.dateTo) {
      filtered = filtered.filter(rsvp => 
        new Date(rsvp.created_at) <= new Date(filters.dateTo + 'T23:59:59')
      );
    }

    // Attending filter
    if (filters.attending) {
      filtered = filtered.filter(rsvp => rsvp.attending === filters.attending);
    }

    // Event Type filter
    if (filters.eventType) {
      if (filters.eventType === '1') {
        filtered = filtered.filter(rsvp => !rsvp.event_2); // Event 1 (event_2 = false)
      } else if (filters.eventType === '2') {
        filtered = filtered.filter(rsvp => rsvp.event_2); // Event 2 (event_2 = true)
      }
    }

    // Search query filter
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(rsvp => 
        rsvp.first_name.toLowerCase().includes(query) ||
        rsvp.last_name.toLowerCase().includes(query) ||
        rsvp.email.toLowerCase().includes(query)
      );
    }

    setFilteredRsvps(filtered);
  }, [rsvps, filters]);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      attending: '',
      eventType: '',
      searchQuery: ''
    });
    setFilteredRsvps(rsvps);
  };

  // Format date for display
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format events list for display
  const formatEvents = (events) => {
    if (!events || events.length === 0) return 'None';
    
    const eventNames = {
      'welcome-party': 'Welcome Party',
      'ceremony-reception': 'Ceremony & Reception',
      'farewell-brunch': 'Farewell Brunch'
    };

    return events.map(event => eventNames[event] || event).join(', ');
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      'First Name',
      'Last Name', 
      'Email',
      'Attending',
      'Number of Guests',
      'Events',
      'Event Type',
      'Dietary Restrictions',
      'Submitted Date'
    ];

    const csvData = filteredRsvps.map(rsvp => [
      rsvp.first_name,
      rsvp.last_name,
      rsvp.email,
      rsvp.attending === 'yes' ? 'Yes' : 'No',
      rsvp.number_of_guests,
      formatEvents(rsvp.events),
      rsvp.event_2 ? 'Event 2' : 'Event 1',
      rsvp.dietary_restrictions || 'None',
      formatDateTime(rsvp.created_at)
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rsvp-submissions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    fetchRsvps();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const attendingCount = filteredRsvps.filter(rsvp => rsvp.attending === 'yes').length;
  const notAttendingCount = filteredRsvps.filter(rsvp => rsvp.attending === 'no').length;
  const totalGuests = filteredRsvps
    .filter(rsvp => rsvp.attending === 'yes')
    .reduce((sum, rsvp) => sum + rsvp.number_of_guests, 0);

  return (
    <div className="rsvp-submissions">
      <div className="rsvp-header">
        <div className="header-title">
          <h1>RSVP Submissions</h1>
          <div className="stats">
            <span className="stat-item">
              <strong>{filteredRsvps.length}</strong> Total
            </span>
            <span className="stat-item attending">
              <strong>{attendingCount}</strong> Attending
            </span>
            <span className="stat-item not-attending">
              <strong>{notAttendingCount}</strong> Not Attending
            </span>
            <span className="stat-item guests">
              <strong>{totalGuests}</strong> Total Guests
            </span>
          </div>
        </div>
        
        <div className="header-actions">
          <button 
            className="btn-secondary refresh-btn" 
            onClick={fetchRsvps}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button 
            className="btn-primary-primary export-btn" 
            onClick={exportToCSV}
            disabled={filteredRsvps.length === 0}
          >
            Export CSV
          </button>
        </div>
      </div>

      <div className="filters-section">
        <div className="filters-row">
          <div className="filter-group">
            <label>Date From:</label>
            <input
              type="date"
              name="dateFrom"
              value={filters.dateFrom}
              onChange={handleFilterChange}
              className="filter-input"
            />
          </div>
          
          <div className="filter-group">
            <label>Date To:</label>
            <input
              type="date"
              name="dateTo"
              value={filters.dateTo}
              onChange={handleFilterChange}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label>Attending:</label>
            <select
              name="attending"
              value={filters.attending}
              onChange={handleFilterChange}
              className="filter-input"
            >
              <option value="">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Event Type:</label>
            <select
              name="eventType"
              value={filters.eventType}
              onChange={handleFilterChange}
              className="filter-input"
            >
              <option value="">All Events</option>
              <option value="1">Event 1</option>
              <option value="2">Event 2</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Search:</label>
            <input
              type="text"
              name="searchQuery"
              value={filters.searchQuery}
              onChange={handleFilterChange}
              placeholder="Name or email..."
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <button 
              className="btn-secondary clear-filters-btn" 
              onClick={clearFilters}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {loading ? (
        <div className="loading">
          Loading RSVP submissions...
        </div>
      ) : (
        <div className="rsvp-table-container">
          <table className="rsvp-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Attending</th>
                <th>Guests</th>
                <th>Event Type</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {filteredRsvps.length === 0 ? (
                <tr>
                  <td colSpan="6" className="no-data">
                    {rsvps.length === 0 ? 'No RSVP submissions found.' : 'No submissions match the current filters.'}
                  </td>
                </tr>
              ) : (
                filteredRsvps.map((rsvp) => (
                  <tr key={rsvp.id} className={`rsvp-row ${rsvp.attending}`}>
                    <td className="name-cell">
                      <strong>{rsvp.first_name} {rsvp.last_name}</strong>
                    </td>
                    <td>{rsvp.email}</td>
                    <td>
                      <span className={`status-badge ${rsvp.attending}`}>
                        {rsvp.attending === 'yes' ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="guests-cell">
                      {rsvp.attending === 'yes' ? rsvp.number_of_guests : '-'}
                    </td>
                    <td className="event-type-cell">
                      <span className={`event-type-badge ${rsvp.event_2 ? 'event-2' : 'event-1'}`}>
                        {rsvp.event_2 ? 'Event 2' : 'Event 1'}
                      </span>
                    </td>
                    <td className="date-cell">
                      {formatDateTime(rsvp.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RsvpSubmissions;
